import { useState } from "react";
import Button from "../ui/Button.jsx";
import {
  mapOryConnectApiError,
  validateOryConnectFields,
} from "./oryConnectValidation.js";
import { ORY_NETWORK_GUIDANCE } from "./oryConnectGuidance.js";
import ProviderFrameworkPicker, { normalizeFrameworksForProviderSubmit } from "./ProviderFrameworkPicker.jsx";
import { getDefaultProviderComplianceFrameworks } from "../../utils/providerFrameworks.js";

/**
 * New-project Ory flow — mirrors ConnectAuth0Panel (POST-ARC.12a).
 *
 * @param {{ onBack: () => void, onConnected: (projectId: string) => void }} props
 */
export default function ConnectOryPanel({ onBack, onConnected }) {
  const [projectSlug, setProjectSlug] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [testOk, setTestOk] = useState(false);
  const [connectedAs, setConnectedAs] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [frameworks, setFrameworks] = useState(() => getDefaultProviderComplianceFrameworks("ory"));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleTest = async () => {
    setError(null);
    setTestOk(false);
    setConnectedAs("");
    const validated = validateOryConnectFields({
      project_slug: projectSlug,
      api_key: apiKey,
    });
    if (!validated.ok) {
      setError(validated.error);
      return;
    }
    setTestBusy(true);
    try {
      const res = await fetch("/api/idp/ory/test-connection", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_slug: validated.project_slug,
          api_key: validated.api_key,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        setError(mapOryConnectApiError(data));
        return;
      }
      setTestOk(true);
      setConnectedAs(String(data.connected_as || data.project_slug || validated.project_slug));
    } catch (e) {
      setError(e?.message || "Test connection failed");
    } finally {
      setTestBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!testOk) return;
    setError(null);
    const validated = validateOryConnectFields({
      project_slug: projectSlug,
      api_key: apiKey,
    });
    if (!validated.ok) {
      setError(validated.error);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/projects/ory", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_slug: validated.project_slug,
          api_key: validated.api_key,
          display_name: displayName.trim() || undefined,
          name: displayName.trim() || undefined,
          complianceFrameworks: normalizeFrameworksForProviderSubmit("ory", frameworks),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.project?.projectId) {
        throw new Error(mapOryConnectApiError(data));
      }
      const projectId = data.project.projectId;
      try {
        sessionStorage.setItem("idwiz.activeProjectId", projectId);
      } catch {
        /* ignore */
      }
      onConnected?.(projectId);
    } catch (err) {
      setError(err?.message || "Could not create project");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "20px 32px 8px" }}>
        <p className="text-secondary" style={{ fontSize: "12px", marginBottom: "16px", whiteSpace: "pre-line" }}>
          {ORY_NETWORK_GUIDANCE}
        </p>

        <label style={{ display: "block", marginBottom: "14px" }}>
          <span className="text-secondary" style={{ fontSize: "12px", display: "block", marginBottom: "6px" }}>
            Project name (optional)
          </span>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Ory assessment"
            style={inputStyle}
          />
        </label>

        <label style={{ display: "block", marginBottom: "14px" }}>
          <span className="text-secondary" style={{ fontSize: "12px", display: "block", marginBottom: "6px" }}>
            Project slug
          </span>
          <input
            required
            value={projectSlug}
            onChange={(e) => {
              setProjectSlug(e.target.value);
              setTestOk(false);
            }}
            placeholder="your-project-slug"
            style={inputStyle}
          />
        </label>

        <label style={{ display: "block", marginBottom: "14px" }}>
          <span className="text-secondary" style={{ fontSize: "12px", display: "block", marginBottom: "6px" }}>
            Project API key
          </span>
          <div style={{ display: "flex", gap: "8px" }}>
            <input
              required
              type={showSecret ? "text" : "password"}
              value={apiKey}
              onChange={(e) => {
                setApiKey(e.target.value);
                setTestOk(false);
              }}
              autoComplete="off"
              style={{ ...inputStyle, flex: 1 }}
            />
            <button type="button" onClick={() => setShowSecret((s) => !s)} style={toggleBtnStyle}>
              {showSecret ? "Hide" : "Show"}
            </button>
          </div>
        </label>

        <div style={{ marginBottom: "16px" }}>
          <div className="text-secondary" style={{ fontSize: "12px", marginBottom: "8px" }}>
            Compliance frameworks
          </div>
          <ProviderFrameworkPicker providerId="ory" value={frameworks} onChange={setFrameworks} />
        </div>

        {testOk ? (
          <p style={{ fontSize: "13px", color: "var(--status-ok, #4ade80)", marginBottom: "8px" }}>
            ✓ Connection verified — Connected as: {connectedAs}
          </p>
        ) : null}

        {error ? (
          <p className="text-critical" style={{ fontSize: "13px" }}>
            {error}
          </p>
        ) : null}
      </div>

      <div style={footerStyle}>
        {!testOk && !testBusy ? (
          <span className="text-secondary" style={{ fontSize: "11px", marginRight: "auto" }}>
            Test connection before creating the project.
          </span>
        ) : null}
        <Button type="button" variant="ghost" onClick={onBack} disabled={loading || testBusy}>
          Back
        </Button>
        <Button type="button" variant="secondary" disabled={testBusy || loading} onClick={() => void handleTest()}>
          {testBusy ? "Testing…" : "Test Connection"}
        </Button>
        <Button type="submit" variant="primary" loading={loading} disabled={loading || !testOk}>
          {loading ? "Creating…" : "Create Project"}
        </Button>
      </div>
    </form>
  );
}

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: "4px",
  border: "1px solid var(--border)",
  background: "var(--bg-input)",
  color: "var(--text-primary)",
  fontSize: "14px",
};

const toggleBtnStyle = {
  background: "var(--bg-elevated)",
  border: "1px solid var(--border)",
  borderRadius: "4px",
  padding: "0 12px",
  cursor: "pointer",
  color: "var(--text-secondary)",
};

const footerStyle = {
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "12px",
  padding: "16px 32px 24px",
  borderTop: "1px solid var(--border)",
};
